import type { CSSProperties } from 'react';

import { cn } from '@/lib/utils';

/**
 * Myndwell wordmark, traced from the supplied Myndwell.svg (827.78 x 185.94).
 *
 * COLOUR: #0f5f8c blue / #f54600 orange. These are the SHAPES from the supplied
 * SVG wearing the COLOURS of the myndwell-logo.png this replaced, and that split
 * is deliberate.
 *
 * The supplied SVG carried #2a527e / #d95000 and shipped that way for one round.
 * On the real sky band it read visibly dull against the brand, so the two
 * dominant opaque colours were sampled straight out of the retired PNG
 * (13,667px of #0f5f8c and 9,751px of #f54600 across its 630x144 canvas) and
 * adopted here. They are also exactly the values `client-theme.css:1076` names
 * as the source of part of the client ramp, so this ends the divergence that
 * file documents rather than deepening it: the wordmark orange is once again
 * `--color-client-warm-500`.
 *
 * If a future asset drop changes these, sample the asset rather than
 * eyeballing — `git show <rev>:public/myndwell-logo.png` still has the original.
 */
const WORDMARK_BLUE_PATHS = [
  'M95.55,72.04l-.26,67.9-26.07.05-.08-65.85c0-4.1-2.09-9.78-4.62-11.95-3.54-3.05-8.78-3.94-14.18-4.1-7.92-.24-16.25,4.42-16.28,13.24l-.23,68.43-25.09.34.34-74.47c.06-13.06,11.54-23.91,23.13-27.58,17.24-5.46,35.01-4.07,50.3,5.44,12.67-7.85,27.31-9.48,42.5-7.21,18.19,2.73,31.84,17.01,31.95,36.11l.41,67.5c-9.21,1.25-26.58,1.98-26.6-10.74l-.09-54.77c-.02-10.69-7.59-16.19-18-16.02-7.57.13-17.09,3.79-17.13,13.68Z',
  'M439.31,139.49c-25.41,5.55-55.75.05-63.19-28.72-6.96-26.91-3.89-70.32,32.7-75.38,14.18-1.96,27.44,3.46,34.88,15.21l.08-42.38c6.87-1.66,16.73-2.04,21.69,2.49,2.62,2.4,4.58,7.27,4.55,11.49l-.52,79.9c-.12,18.65-12.47,33.52-30.19,37.39ZM440.2,108.4c6.4-15.28,6.28-48.51-16.01-49.91-8.91-.56-17.06,2.81-20.56,11.94-4.66,12.16-4.92,26.52.41,38.51,3.41,7.67,11.22,10.73,18.42,10.37,7.02-.35,14.46-3.07,17.74-10.91Z',
  'M221.07,160.41c-7.13,21.07-25.36,20.83-44.04,19.22l-.02-21.15c7.18-.54,11.67-.66,16.99-2.02,6.31-1.61,8.36-10.75,6.25-16.81l-35.59-102.59,29.25-.43,20.59,70.6,17.95-61.69c1.51-5.18,5.78-8.33,11.03-9.08l19.23.8-41.65,123.16Z',
  'M336.66,74.86c0-4.89-1.97-10.43-4.49-12.57-3.81-3.23-8.93-4.13-14.2-4.24-8.13-.17-16.3,4.35-16.32,13.21l-.18,68.71-26.62.02.35-68.02c.07-12.72,7.11-25.74,18.35-31.3,15.57-7.7,33.87-7.77,49.67-.74,12.04,5.36,19.26,18.28,19.34,31.63l.39,68.19c-10.37,1.07-26.33,1.56-26.32-11.71l.02-53.19Z',
] as const;

const WORDMARK_ORANGE_PATHS = [
  'M592.58,46.03c2.74-11.99,20.25-10.15,29.55-8.18l-28.37,102.13-26.94-.37-15.74-71.7-16.27,71.85-27.1.15-28.2-102.81,26.26-.39,16.42,69.53,14.97-69.66,27.33.27,14.07,70.45,14.01-61.27Z',
  'M779.24,18.56l.06,121.16h-25.94s0-132.08,0-132.08c5.38-.2,11.51-.28,15.91,0,3.69.24,9.96,6.5,9.96,10.91Z',
  'M823.59,18.62l.06,121.2-25.79.08-.02-132.38c9.06-1.5,25.74-.63,25.75,11.1Z',
] as const;

/**
 * The brain-doodle "e" — three paths lifted from the wordmark above. Bounding
 * box measured in headless Chromium at x 629.80, y 35.07, w 104.71, h 106.96,
 * which is why the group is translated back to a 0-origin: the component ships
 * its own tight viewBox so callers can size it like any other icon.
 *
 * Shared by the favicon (public/favicon.svg) and the collapsed admin sidebar
 * rail (SidebarBrand.tsx). Kept beside the wordmark so the two cannot drift.
 */
export const GLYPH_PATHS = [
  'M708.14,108.52l23.79-.23c-5.92,32.55-43.52,38.16-69.45,30.97-18.47-5.12-31.07-20.55-31.14-39.9l26.36.11,7.82,12.57c6.6,10.6,22.27,8.74,32.06,2.53,2.61-1.65,7.72-6.02,10.55-6.05Z',
  'M658.87,77.67c-7.41,3.92-10.66-10.08-23.69-6.45-.55-1.42-1.22-6.61-.42-8.53s4.85-4.48,6.3-4.54l7.83,7.39c.4.38,3.87-.43,3.62-.92-2.91-5.73-10.67-8-6.97-15.41,1.54-3.07,8.13-7.91,10.72-4.75,2.31,8.23,6.29,16.71,16.7,12.83l-7.84-4.7c-3.31-1.98-4.96-7.45-3.64-11.35,1.12-3.3,5.59-6.05,9.55-6.17,3.03-.09,8.69,3.98,8.71,7.76l.17,42.16c.02,3.77-4.24,8.74-8.28,8.7l-34.91-.31c-6.46-.06-8.64-9.54-5.51-13.75,3.34-4.5,10.03-7.48,15.24-1.79,2.64,2.88,8.14,4.6,12.43,3.86,7.4-1.28,9.98-12.83,7.51-11.86-6.5,2.54-1.42,4.6-7.52,7.83Z',
  'M693.27,66.58c3.42-10.62,18.48,10.18,29.77-8.72,2.22.86,5.33,3.01,6.46,4.79,1.24,1.96.68,8-.92,8.52-4.92.15-10.35.46-13.29,3.57-2.5,2.64-5.11,8.25-1.14,11.47,2.36-6.33,4.74-11.07,9.58-11.42,5.97-.43,10.12,3.87,10.66,7.9.97,7.32-3.91,11.1-10.55,11.09l-29.67-.02c-6.08,0-9.5-4.76-9.5-10.41l-.03-37.53c0-3.94,1.85-7.98,5.09-9.75,2.99-1.63,8.29-1.04,10.74,1.46s2.68,7.04,2.67,10.78v3.02c-.03,6.34,4.79-7.02,6.48-7.71,2.83-1.14,8.29,3.03,9.18,5.77,1.14,3.49.15,8.48-2.42,10.82-8.6,7.82-16.42-7.66-24.7,2.06-3.34,3.92-2.89,9.22-.28,12.9,2.82,3.97,10.52,3.67,13.05,1.23,6.95-6.7-5.64.59-9.78-3.37-1.29-1.23-1.94-4.75-1.4-6.45Z',
] as const;

/** Glyph bbox inside the wordmark's coordinate space. */
const GLYPH_ORIGIN = { x: 629.8, y: 35.07, width: 104.71, height: 106.96 } as const;

export function MyndwellGlyph({ className, size = 24 }: { className?: string; size?: number }) {
  return (
    <svg
      width={size}
      height={(size * GLYPH_ORIGIN.height) / GLYPH_ORIGIN.width}
      viewBox={`0 0 ${String(GLYPH_ORIGIN.width)} ${String(GLYPH_ORIGIN.height)}`}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={className}
    >
      {/* currentColor so a host surface can tint it — the contract the admin
          sidebar's collapsed rail already relies on. */}
      <g
        fill="currentColor"
        transform={`translate(${String(-GLYPH_ORIGIN.x)},${String(-GLYPH_ORIGIN.y)})`}
      >
        {GLYPH_PATHS.map((d) => (
          <path key={d.slice(0, 24)} d={d} />
        ))}
      </g>
    </svg>
  );
}

export type BrandSize = 'sm' | 'md';
export type BrandTone = 'ink' | 'light';

interface BrandMarkBaseProps {
  /** `sm` — compact chrome size. `md` — hero size. */
  size?: BrandSize;
  /** `ink` — full-colour. `light` — reversed to white for dark surfaces. */
  tone?: BrandTone;
  /**
   * MyndwellMark only — explicit pixel height for the inline SVG wordmark,
   * overriding the `size` token. Lets chrome surfaces (top nav, sidebar) tune
   * the mark to their bar height without a new size tier.
   */
  height?: number;
  className?: string;
}

/**
 * Size tokens for the myndwell mark. A function rather than a constant so the
 * `sm`/`md` tiers stay legible at the call site.
 */
function brandTokens(size: BrandSize) {
  // myndwell wordmark SVG is 827.78x185.94 (~4.45:1). Pin height; width follows.
  const primaryHeight = size === 'sm' ? 22 : 36;
  return { primaryHeight };
}

/**
 * myndwell product mark. Used in all chrome — top nav, sidebar header, auth
 * brand panels, error/utility cards.
 */
export function MyndwellMark({ size = 'md', tone = 'ink', height, className }: BrandMarkBaseProps) {
  const t = brandTokens(size);
  const isLight = tone === 'light';
  const blue = isLight ? '#ffffff' : '#0f5f8c';
  const orange = isLight ? '#ffffff' : '#f54600';
  return (
    <span className={cn('inline-flex items-center', className)}>
      {/* Height flows through a CSS variable + class, NOT an inline style:
          an inline `height` beats every class, which silently killed caller
          overrides like AuthLayout's `lg:[&_svg]:h-[34px]` and AuthHeading's
          `max-lg:[&_svg]:h-5` (found in the 2026-08-08 pre-merge audit).
          The style attr only DEFINES the variable; the class consumes it, so
          a caller's descendant rule (higher specificity) wins as intended. */}
      <svg
        viewBox="0 0 827.78 185.94"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        style={{ '--brand-mark-h': `${String(height ?? t.primaryHeight)}px` } as CSSProperties}
        className="h-(--brand-mark-h) w-auto shrink-0 select-none"
      >
        <g fill={blue}>
          {WORDMARK_BLUE_PATHS.map((d) => (
            <path key={d.slice(0, 24)} d={d} />
          ))}
        </g>
        <g fill={orange}>
          {[...WORDMARK_ORANGE_PATHS, ...GLYPH_PATHS].map((d) => (
            <path key={d.slice(0, 24)} d={d} />
          ))}
        </g>
      </svg>
      <span className="sr-only">myndwell</span>
    </span>
  );
}
